import React, { ReactNode, memo, useEffect, useRef, useState } from 'react';

import { Collapse } from '@material-ui/core';
import { Lock } from 'icons';
import { useIntl } from 'localization';
import { twMerge } from 'tailwind-merge';

import Badge from '../../../atoms/Badge';
import { useScrollText } from '../../../hooks/useScrollText';
import { DropdownExpandMoreIcon } from '../../Dropdown/DropdownExpandMoreIcon';
import { DropdownItem } from '../../Dropdown/DropdownItem';

type TabData = {
  value: string;
  label: ReactNode;
  locked?: boolean;
  isNew?: boolean;
  icon?: ReactNode;
};

export type AdminSettingsDisclosureProps = {
  className?: string;
  sectionName: ReactNode;
  sectionCount?: ReactNode;
  openTab: string;
  onChangeTab(newTab: string): void;
  data?: TabData[];
  exact?: boolean;
  icon?: ReactNode;
  tabIndex: number;
  link?: string;
  wrapperClassName?: string;
  saveState?: boolean;
  localStorageKey?: string;
};

export const AdminSettingsDisclosure = ({
  className,
  sectionName,
  sectionCount,
  data,
  openTab,
  onChangeTab,
  exact = true,
  icon,
  tabIndex,
  link,
  wrapperClassName,
  saveState = true,
  localStorageKey = 'adminSettingsTabIndex',
}: AdminSettingsDisclosureProps) => {
  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(
    saveState
      ? tabIsOpened(tabIndex, localStorageKey) || data?.some((x) => openTab?.includes(x.value))
      : false,
  );

  const textBoxRef = useRef<HTMLDivElement>(null);
  const triggerBoxRef = useRef<HTMLDivElement>(null);

  const isSelected =
    data?.some((x) => openTab?.includes(x.value)) || (link ? openTab?.includes(link) : false);

  useEffect(() => {
    if (!isOpen && data?.some((x) => openTab?.includes(x.value))) {
      setIsOpen(true);
    }
  }, [openTab]);

  useScrollText(textBoxRef, triggerBoxRef);

  return (
    <div className={twMerge('text-sm', className)}>
      <DropdownItem
        ref={triggerBoxRef}
        wrapperClassName={wrapperClassName}
        onClick={() => {
          if (link) {
            onChangeTab(link);
            return;
          }
          setIsOpen((prev) => !prev);
          if (saveState) {
            saveTabStateToLocalStorage(tabIndex, !isOpen, localStorageKey);
          }
        }}
        className={twMerge('group w-full gap-[6px] capitalize mb-0', isOpen && 'font-semibold')}
        icon={
          icon ? (
            <span className={!isSelected ? 'text-v2blueGray-550' : ''}>{icon}</span>
          ) : data ? (
            <DropdownExpandMoreIcon open={isOpen} />
          ) : null
        }
        selected={isSelected}
      >
        <div className="flex justify-between items-center w-full gap-2 truncate">
          <span ref={textBoxRef} className="truncate">
            <span className="group-hover:inline-block">{sectionName}</span>
          </span>
          {sectionCount !== undefined && (
            <div className={twMerge('w-min', !isSelected && 'text-v2blueGray-500')}>
              {sectionCount}
            </div>
          )}
        </div>
      </DropdownItem>

      <Collapse in={isOpen}>
        <div className="pl-6 flex flex-col">
          {data?.map((entry: TabData, index) => {
            const selected = exact ? openTab === entry.value : openTab.startsWith(entry.value);

            return (
              <DropdownItem
                key={`${index}${entry.value}`}
                wrapperClassName={twMerge('relative', wrapperClassName)}
                disabled={entry.locked}
                icon={entry.locked ? <Lock className="w-5 h-5 text-v2blueGray-500" /> : entry.icon}
                className={twMerge(
                  'select-none',
                  entry.locked && 'text-v2blueGray-550 justify-between',
                  !entry.locked && !selected && 'text-v2blueGray-700',
                )}
                iconPosition={entry.icon ? 'left' : 'right'}
                selected={selected}
                ref={
                  selected
                    ? (el) => {
                        if (el) {
                          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        }
                      }
                    : undefined
                }
                onClick={() => {
                  onChangeTab(entry.value);
                }}
              >
                {entry.label}
                {entry.isNew && (
                  <div className="absolute top-1/2 -translate-y-1/2 right-2">
                    <Badge
                      text={formatMessage({ id: 'conversationsHub.new' })}
                      theme="purple-dark"
                    />
                  </div>
                )}
              </DropdownItem>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

const tabIsOpened = (tabIndex: number, localStorageKey: string) => {
  const allTabsData = localStorage.getItem(localStorageKey);
  if (allTabsData) {
    const parsedData = JSON.parse(allTabsData);
    return !!parsedData[tabIndex];
  }
  return false;
};

const saveTabStateToLocalStorage = (tabIndex: number, isOpen: boolean, localStorageKey: string) => {
  const allTabsData = localStorage.getItem(localStorageKey);
  const parsedData = allTabsData ? JSON.parse(allTabsData) : {};
  parsedData[tabIndex] = isOpen;
  localStorage.setItem(localStorageKey, JSON.stringify(parsedData));
};

export default memo(AdminSettingsDisclosure);
